import type { ModelConfig } from '@meridian-ssa/shared';
import { useEffect, useState } from 'react';

import { EvalLayout } from '@/components/eval/EvalLayout';
import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

export default function ConfigsPage() {
  const [configs, setConfigs] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfigs();
  }, []);

  async function loadConfigs() {
    setLoading(true);
    try {
      const data = await apiRequest('/api/eval/configs');
      setConfigs(data.configs || []);
    } catch (e) {
      logError('Error loading configs', e);
    } finally {
      setLoading(false);
    }
  }

  async function toggleEnabled(id: string, currentEnabled: boolean) {
    try {
      await apiRequest(`/api/eval/configs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !currentEnabled }),
      });
      loadConfigs();
    } catch (e) {
      logError('Error toggling config enabled', e);
      alert('Failed to update config');
    }
  }

  const enabledConfigs = configs.filter(c => c.enabled !== false);
  const disabledConfigs = configs.filter(c => c.enabled === false);

  const renderConfigTable = (
    configList: ModelConfig[],
    title: string,
    showToggle: boolean
  ) => {
    if (configList.length === 0) {
      return (
        <div className="card text-center p-8">
          <p className="text-muted m-0">
            No {title.toLowerCase()} configurations.
          </p>
        </div>
      );
    }

    return (
      <div className="card p-0 overflow-hidden">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[rgba(71,51,31,0.03)] border-b border-[#E8E3DD]">
              <th className="px-5 py-3.5 text-left text-sm font-semibold text-text">
                Name
              </th>
              <th className="px-5 py-3.5 text-left text-sm font-semibold text-text">
                Provider
              </th>
              <th className="px-5 py-3.5 text-left text-sm font-semibold text-text">
                Model
              </th>
              <th className="px-5 py-3.5 text-left text-sm font-semibold text-text">
                Temperature
              </th>
              <th className="px-5 py-3.5 text-left text-sm font-semibold text-text">
                Max Tokens
              </th>
              {showToggle && (
                <th className="px-5 py-3.5 text-right text-sm font-semibold text-text">
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {configList.map(config => (
              <tr key={config.id} className="border-b border-[#E8E3DD]">
                <td className="px-5 py-3.5 text-[0.9375rem] text-text font-medium">
                  {config.name}
                </td>
                <td className="px-5 py-3.5 text-sm text-text-light">
                  {config.provider}
                </td>
                <td className="px-5 py-3.5 text-sm text-text-light">
                  {config.api_model}
                </td>
                <td className="px-5 py-3.5 text-sm text-text-light">
                  {config.temperature ?? 'default'}
                </td>
                <td className="px-5 py-3.5 text-sm text-text-light">
                  {config.max_tokens ?? 'default'}
                </td>
                {showToggle && (
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={() =>
                        toggleEnabled(config.id, config.enabled !== false)
                      }
                      className={`
                        px-3.5 py-1.5 border-none rounded-md text-[0.8125rem] font-medium cursor-pointer
                        ${
                          config.enabled !== false
                            ? 'bg-accent/10 text-accent'
                            : 'bg-text-light/10 text-text-light'
                        }
                      `}
                    >
                      {config.enabled !== false ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <EvalLayout>
      <header className="mb-8">
        <h1 className="text-[2rem] font-medium text-text m-0">
          Model Configurations
        </h1>
        <p className="text-text-light mt-2 text-[0.9375rem]">
          Manage which model configurations are used in the arena. Only enabled
          configs will be paired in matches.
        </p>
      </header>

      {loading ? (
        <div className="text-center p-16 text-muted">
          Loading configurations...
        </div>
      ) : (
        <div className="flex flex-col gap-10">
          <section>
            <h2 className="text-xl font-semibold text-text mb-4">
              ✓ Enabled Configurations
            </h2>
            <p className="text-text-light text-sm mb-4">
              These configurations are active and will be used for arena
              matches.
            </p>
            {renderConfigTable(enabledConfigs, 'Enabled', true)}
          </section>

          <section>
            <h2 className="text-xl font-semibold text-muted mb-4">
              ✕ Disabled Configurations
            </h2>
            <p className="text-muted text-sm mb-4">
              These configurations are currently not used in the arena.
            </p>
            {renderConfigTable(disabledConfigs, 'Disabled', true)}
          </section>
        </div>
      )}
    </EvalLayout>
  );
}
